/**
 * POS Cart Drawer — subscription-only cart drawer with dynamic gift states.
 */
const POS_CART_QTY_DEBOUNCE_MS = 200;
const POS_CART_DRAWER_MONEY_FORMAT = '$\u007B\u007Bamount\u007D\u007D';

class PosCartDrawer {
  constructor(root) {
    this.root = root;
    this.config = this.#parseConfig();
    this.qtyPending = new Map();
    this.bundleFamilies = this.#buildBundleFamilies();
    this.variantCompareAtPrices = new Map();
    this.#seedVariantCompareAtPricesFromDom();
    this.#seedVariantCompareAtPricesFromBundles();
    this.scrollEl = root.querySelector('.js-pos-cart-drawer-scroll');
    this.bodyEl = root.querySelector('.js-pos-cart-drawer-body');
    this.footerEl = root.querySelector('.js-pos-cart-drawer-footer');
    this.rewardBarEl = root.querySelector('.js-pos-cart-drawer-reward-bar');
    this.subtotalEl = root.querySelector('.js-pos-cart-drawer-subtotal');
    this.subtotalCompareEl = root.querySelector('.js-pos-cart-drawer-subtotal-compare');
    this.subtotalPriceEl = root.querySelector('.js-pos-cart-drawer-subtotal-price');
    this.shippingPriceEl = root.querySelector('.js-pos-cart-drawer-shipping-price');
    this.headerCheckEl = root.querySelector('.js-pos-cart-drawer-header-check');
    this.headerCountEl = root.querySelector('.js-pos-cart-drawer-header-count');
    this.checkoutBtn = root.querySelector('.js-pos-cart-drawer-checkout');
    this.checkoutLabelEl = root.querySelector('.js-pos-cart-drawer-checkout-label');

    for (const button of root.querySelectorAll('.js-pos-cart-drawer-close')) {
      button.addEventListener('click', () => this.close());
    }

    document.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') this.close();
    });

    this.bodyEl?.addEventListener('click', (event) => this.#handleBodyClick(event));
    this.boundPreventBackgroundScroll = this.#preventBackgroundScroll.bind(this);
  }

  open() {
    if (this.root.classList.contains('pos-cart-drawer--open')) return;

    this.root.classList.add('pos-cart-drawer--open');
    this.#lockPageScroll();
    this.root.setAttribute('aria-hidden', 'false');
  }

  close() {
    if (!this.root.classList.contains('pos-cart-drawer--open')) return;

    this.root.classList.remove('pos-cart-drawer--open');
    this.#unlockPageScroll();
    this.root.setAttribute('aria-hidden', 'true');
    this.#closeAllPlanSelects();
  }

  #lockPageScroll() {
    document.documentElement.classList.add('overflow-hidden');
    document.body.classList.add('overflow-hidden');
    document.addEventListener('wheel', this.boundPreventBackgroundScroll, { passive: false });
    document.addEventListener('touchmove', this.boundPreventBackgroundScroll, { passive: false });
  }

  #unlockPageScroll() {
    document.documentElement.classList.remove('overflow-hidden');
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('wheel', this.boundPreventBackgroundScroll);
    document.removeEventListener('touchmove', this.boundPreventBackgroundScroll);
  }

  #preventBackgroundScroll(event) {
    if (this.scrollEl?.contains(event.target)) return;
    event.preventDefault();
  }

  async refresh() {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await this.#reconcileFreeGift(await response.json());
      this.#render(cart);
      document.dispatchEvent(new CustomEvent('pos-cart-drawer:synced', { detail: { cart } }));
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Free gift with subscription.
   *
   * The offer is "any subscription in the cart earns a free bag", so the gift
   * line is derived from cart state rather than from whichever button the
   * customer pressed. Every path that mutates the cart funnels its resulting
   * cart object through here, which adds the gift line when a subscription
   * appears and pulls it back out when the last subscription leaves.
   *
   * The gift product itself is priced at $0 in the admin (compare-at $69), so
   * no discount is involved. The hidden `_free_gift` property is only a marker
   * for the line the drawer added. Because the product is free, the reconcile
   * also strips any copy that arrived another way, and keeps the one it owns
   * at quantity 1.
   *
   * The gift is deliberately kept out of #getDrawerItems, so it never renders
   * as an ordinary card with a quantity stepper and a remove button, and never
   * lands in the drawer's item count or subtotal. It gets its own read-only row
   * in #renderBody instead.
   */
  #isFreeGiftItem(item) {
    // Strictly inert unless this drawer is running the gift offer: the drawer
    // script is shared by templates that know nothing about it, and they must
    // not start hiding cart lines that happen to carry the same property.
    const gift = this.config.freeGift;
    if (!gift?.variantId) return false;

    // Matched by variant too, not just the property: the gift product is $0,
    // so a copy added outside the drawer (a cart permalink, /cart/add, the
    // cart page) has to be caught here as well, or it would render as an
    // ordinary product and ride along to checkout for free.
    return (
      Number(item?.variant_id) === Number(gift.variantId) ||
      item?.properties?.[gift.propertyKey || '_free_gift'] === 'true'
    );
  }

  // The one gift line the drawer itself would have added: the configured
  // variant, carrying the hidden marker. Anything else that matches
  // #isFreeGiftItem is a stray copy.
  #isOwnFreeGiftLine(item) {
    const gift = this.config.freeGift;
    return (
      Number(item?.variant_id) === Number(gift.variantId) &&
      item?.properties?.[gift.propertyKey || '_free_gift'] === 'true'
    );
  }

  #findFreeGiftLine(cart) {
    return (cart?.items || []).find((item) => this.#isFreeGiftItem(item)) || null;
  }

  #findSubscriptionLine(cart) {
    return this.#getDrawerItems(cart?.items ?? []).find((entry) => entry.isSubscription) || null;
  }

  /**
   * Brings the cart to exactly one gift line, at quantity 1, and only while a
   * subscription is present. Stray copies are removed rather than adopted, so
   * the line that survives is always one the drawer added.
   */
  async #reconcileFreeGift(cart) {
    const gift = this.config.freeGift;
    if (!gift?.variantId) return cart;

    const parentEntry = this.#findSubscriptionLine(cart);
    const giftLines = (cart?.items || []).filter((item) => this.#isFreeGiftItem(item));
    const keeper = parentEntry ? giftLines.find((item) => this.#isOwnFreeGiftLine(item)) || null : null;

    const updates = {};
    for (const line of giftLines) {
      if (line !== keeper) updates[line.key] = 0;
    }
    if (keeper && keeper.quantity !== 1) updates[keeper.key] = 1;

    const hasUpdates = Object.keys(updates).length > 0;
    const needsGift = Boolean(parentEntry) && !keeper;
    if (!hasUpdates && !needsGift) return cart;

    try {
      let updated = hasUpdates ? await this.#updateCartLines(updates) : cart;
      if (needsGift && updated) {
        updated = (await this.#addFreeGiftLine(gift, parentEntry.item.key)) ?? updated;
      }

      return updated ?? cart;
    } catch (error) {
      console.error(error);
      return cart;
    }
  }

  async #addFreeGiftLine(gift, parentLineKey) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: Number(gift.variantId),
            quantity: 1,
            // The parent is already in the cart here, so it is addressed by
            // line key rather than by variant id.
            parent_line_key: parentLineKey,
            properties: { [gift.propertyKey || '_free_gift']: 'true' },
          },
        ],
      }),
    });

    const data = await response.json();
    if (data.status) {
      console.error('Free gift could not be added:', data.description);
      return null;
    }

    return this.#fetchCart();
  }

  // Sets several lines' quantities in one request, keyed by line key.
  async #updateCartLines(updates) {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates }),
    });

    const data = await response.json();
    if (data.status) {
      console.error('Free gift lines could not be updated:', data.description);
      return null;
    }

    return data;
  }

  async #fetchCart() {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return response.json();
  }

  getDrawerItemCount(cart) {
    return this.#getDrawerItems(cart?.items ?? []).reduce(
      (sum, entry) => sum + entry.item.quantity,
      0
    );
  }

  #parseConfig() {
    const node = this.root.querySelector('.js-pos-cart-drawer-config');
    if (!node?.textContent) return { families: [], strings: {}, gifts: [], recommendations: [] };

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('POS cart drawer config parse error', error);
      return { families: [], strings: {}, gifts: [], recommendations: [] };
    }
  }

  #handleBodyClick(event) {
    const target = event.target;

    if (!target.closest('[data-plan-select]')) {
      this.#closeAllPlanSelects();
    }

    const planTrigger = target.closest('.js-pos-cart-drawer-plan-trigger');
    if (planTrigger) {
      event.preventDefault();
      if (planTrigger.closest('.pos-cart-drawer__plan-controls--loading')) return;
      this.#togglePlanSelect(planTrigger.closest('[data-plan-select]'));
      return;
    }

    const planOption = target.closest('.js-pos-cart-drawer-plan-option');
    if (planOption) {
      event.preventDefault();
      if (planOption.disabled) return;
      void this.#switchPlan(
        planOption.dataset.line,
        planOption.dataset.familyId,
        planOption.dataset.planId,
        planOption
      );
      return;
    }

    const planSwitchBtn = target.closest('.js-pos-cart-drawer-plan-switch');
    if (planSwitchBtn) {
      event.preventDefault();
      if (planSwitchBtn.disabled || planSwitchBtn.closest('.pos-cart-drawer__plan-controls--loading')) return;
      void this.#switchPlan(
        planSwitchBtn.dataset.line,
        planSwitchBtn.dataset.familyId,
        planSwitchBtn.dataset.planId,
        planSwitchBtn
      );
      return;
    }

    const removeBtn = target.closest('.js-pos-cart-drawer-remove');
    if (removeBtn) {
      event.preventDefault();
      if (removeBtn.disabled) return;
      this.#removeItem(removeBtn.dataset.line, removeBtn);
      return;
    }

    const qtyBtn = target.closest('.js-pos-cart-drawer-qty');
    if (qtyBtn) {
      event.preventDefault();
      if (qtyBtn.disabled) return;

      const card = qtyBtn.closest('.pos-cart-drawer__card');
      const qtyEl = card?.querySelector('.pos-cart-drawer__qty');
      if (qtyEl?.classList.contains('pos-cart-drawer__qty--loading')) return;

      this.#queueQuantityChange(qtyBtn.dataset.line, Number(qtyBtn.dataset.quantity), qtyBtn);
      return;
    }

    const upgradeBtn = target.closest('.js-pos-cart-drawer-upgrade');
    if (upgradeBtn) {
      event.preventDefault();
      if (upgradeBtn.disabled) return;
      this.#upgradeItem(upgradeBtn.dataset.line, upgradeBtn.dataset.familyId, upgradeBtn);
      return;
    }

    const addRecBtn = target.closest('.js-pos-cart-drawer-add-rec');
    if (addRecBtn) {
      event.preventDefault();
      if (addRecBtn.disabled) return;
      this.#addRecommendation(addRecBtn.dataset.variantId, addRecBtn.dataset.sellingPlanId, addRecBtn);
      return;
    }

    const shopNowBtn = target.closest('.js-pos-cart-drawer-shop-now');
    if (shopNowBtn) {
      event.preventDefault();
      this.close();
      return;
    }
  }

  #render(cart) {
    this.#clearAllQtyPending();
    this.#updateCounters(cart);

    const drawerItems = this.#getDrawerItems(cart.items);
    const hasItems = drawerItems.length > 0;
    const aggregateState = this.#getAggregateGiftState(drawerItems);

    this.root.classList.toggle('pos-cart-drawer--empty', !hasItems);
    this.root.dataset.giftState = hasItems ? aggregateState : 'empty';

    this.#renderHeader(hasItems, cart?.item_count ?? 0);
    this.#renderRewardBar(hasItems, aggregateState, drawerItems, cart);
    this.#renderBody(cart, drawerItems, hasItems, aggregateState);
    this.#renderFooter(cart, drawerItems, hasItems);
  }

  #renderHeader(hasItems, itemCount = 0) {
    const headerTitle = this.root.querySelector('.js-pos-cart-drawer-header-title');
    if (!headerTitle) return;

    const strings = this.config.strings || {};

    if (hasItems) {
      headerTitle.textContent = strings.header_added || 'An Item Was Added to Cart';

      if (this.headerCheckEl) {
        this.headerCheckEl.hidden = false;
        this.headerCheckEl.setAttribute('aria-hidden', 'false');
      }

      if (this.headerCountEl) this.headerCountEl.hidden = true;
      return;
    }

    headerTitle.textContent = strings.cart_title || 'Your Cart';

    if (this.headerCheckEl) {
      this.headerCheckEl.hidden = true;
      this.headerCheckEl.setAttribute('aria-hidden', 'true');
    }

    if (this.headerCountEl) {
      this.headerCountEl.hidden = false;
      this.headerCountEl.textContent = String(itemCount);
    }
  }

  #getDrawerItems(items) {
    if (!items?.length) return [];

    // The free gift is presented as its own read-only row, so it must not be
    // treated as an ordinary drawer line here — that would give it a quantity
    // stepper and a remove button, and fold it into the count and subtotal.
    const visible = items.filter((item) => !this.#isFreeGiftItem(item));
    const visibleKeys = new Set(visible.map((item) => item.key));

    // A bundle arrives as a parent line with its products nested underneath.
    // Those components belong inside the parent's card, not beside it, so they
    // are pulled out of the top level and handed to the parent entry. A nested
    // line whose parent is not itself in view is treated as a root, so it can
    // never vanish from the drawer entirely.
    const childrenByParent = new Map();
    for (const item of visible) {
      const parentKey = item.parent_relationship?.parent_key;
      if (!parentKey || !visibleKeys.has(parentKey)) continue;

      const siblings = childrenByParent.get(parentKey) || [];
      siblings.push(item);
      childrenByParent.set(parentKey, siblings);
    }

    const isChild = (item) => {
      const parentKey = item.parent_relationship?.parent_key;
      return Boolean(parentKey && visibleKeys.has(parentKey));
    };

    return visible.filter((item) => !isChild(item)).map((item) => {
      const children = (childrenByParent.get(item.key) || []).map((child) =>
        this.#buildDrawerEntry(child, [])
      );

      return this.#buildDrawerEntry(item, children);
    });
  }

  #buildDrawerEntry(item, children) {
    const family = this.#findFamilyForItem(item);
    const currentPlanId = Number(item.selling_plan_allocation?.selling_plan?.id);
    const isSubscription = Number.isFinite(currentPlanId);

    const bestPlan = family ? this.#getBestPlan(family.plans) : null;
    const currentPlan =
      family && isSubscription
        ? family.plans?.find((plan) => Number(plan.sellingPlanId) === currentPlanId)
        : null;
    const isBest = Boolean(
      family && isSubscription && bestPlan && currentPlanId === Number(bestPlan.sellingPlanId)
    );

    const entry = { item, children, family, currentPlan, bestPlan, isBest, isSubscription };
    if (children.length) return this.#foldBundleEntry(entry);

    return {
      ...entry,
      giftState: isBest ? 'activated' : isSubscription ? 'not-activated' : 'one-time',
    };
  }

  // A bundle's parent is a $0 container that belongs to no product family, so
  // on its own it would read as a one-time line and cost the set its reward
  // progress. The state the shopper is owed lives in the products underneath,
  // which is where it is read from — the same "any line qualifies" rule that
  // applied when they sat at the top level as siblings.
  #foldBundleEntry(entry) {
    const isBest = entry.children.some((child) => child.isBest);
    const isSubscription =
      entry.isSubscription || entry.children.some((child) => child.isSubscription);

    const bundleFamily = this.#findBundleFamilyForItem(entry.item);

    return {
      ...entry,
      bundleFamily,
      bundlePlan: this.#findBundlePlanForItem(bundleFamily, entry.item),
      bundleBestPlan: bundleFamily ? this.#getBestPlan(bundleFamily.plans) : null,
      isBest,
      isSubscription,
      giftState: isBest ? 'activated' : isSubscription ? 'not-activated' : 'one-time',
    };
  }

  // A bundle's price is the sum of its components; the parent contributes the
  // $0 it was created to contribute.
  #getEntryLinePrice(entry) {
    return entry.children.reduce(
      (sum, child) => sum + child.item.final_line_price,
      entry.item.final_line_price
    );
  }

  #getEntryComparePrice(entry) {
    const lines = [entry.item, ...entry.children.map((child) => child.item)];
    const compareTotal = lines.reduce(
      (sum, line) => sum + (this.#getCompareLinePrice(line) ?? line.final_line_price),
      0
    );

    return compareTotal > this.#getEntryLinePrice(entry) ? compareTotal : null;
  }

  #findFamilyForItem(item) {
    const productId = Number(item.product_id);
    if (!Number.isFinite(productId)) return null;

    return (
      this.config.families?.find((family) =>
        family.productIds?.some((id) => Number(id) === productId)
      ) || null
    );
  }

  // A bundle is given the shape of a product family so its card can reuse what
  // a product card already has: the cadence dropdown, the switch to the best
  // plan and the unlocked notice. Its "plans" are the purchase options the card
  // offers, each carrying the parent and the products that option adds.
  //
  // An option with no parent variant is dropped. Re-adding through it would
  // bring the set back ungrouped — six loose cards where the shopper was
  // standing on one — so it is not offered as somewhere to switch to.
  #buildBundleFamilies() {
    return (this.config.bundles || [])
      .map((bundle) => ({
        id: `bundle:${bundle.id}`,
        isBundle: true,
        plans: (bundle.plans || []).filter(
          (plan) => Number(plan?.parentVariantId) > 0 && plan.items?.length
        ),
      }))
      .filter((family) => family.plans.length > 0);
  }

  #findFamilyById(familyId) {
    if (!familyId) return null;

    return (
      this.config.families?.find((family) => family.id === familyId) ||
      this.bundleFamilies.find((family) => family.id === familyId) ||
      null
    );
  }

  // The parent line names the supply the set was bought on — it is the only
  // thing that differs between the options — so both the bundle and the option
  // it currently sits on are read back off its variant id.
  #findBundleFamilyForItem(item) {
    const variantId = Number(item?.variant_id);
    if (!Number.isFinite(variantId)) return null;

    return (
      this.bundleFamilies.find((family) =>
        family.plans.some((plan) => Number(plan.parentVariantId) === variantId)
      ) || null
    );
  }

  #findBundlePlanForItem(family, item) {
    const variantId = Number(item?.variant_id);

    return family?.plans.find((plan) => Number(plan.parentVariantId) === variantId) || null;
  }

  #seedVariantCompareAtPricesFromDom() {
    for (const input of document.querySelectorAll('input[data-variant-id][data-compare-at-price]')) {
      this.setVariantCompareAtPrice(input.dataset.variantId, input.dataset.compareAtPrice);
    }
  }

  // The card only puts the supply it is currently standing on into the DOM, so
  // a bundle switched to the other supply from in here would land with no
  // compare-at price and lose its strike-through. Every option a bundle can be
  // moved to is seeded up front instead.
  #seedVariantCompareAtPricesFromBundles() {
    for (const family of this.bundleFamilies) {
      for (const plan of family.plans) {
        for (const entry of plan.items || []) {
          this.setVariantCompareAtPrice(entry.id, entry.compare_at_price);
        }
      }
    }
  }

  setVariantCompareAtPrice(variantId, compareAtPrice) {
    if (!variantId) return;

    const compareAt = Number(compareAtPrice);
    if (!Number.isFinite(compareAt) || compareAt <= 0) return;

    this.variantCompareAtPrices.set(String(variantId), compareAt);
  }

  #usesCartComparePrice(item) {
    const title = String(item.product_title || '').toLowerCase();
    if (title.includes('primal queen starter kit')) return true;

    const handle = String(item.handle || '').toLowerCase();
    return handle.includes('primal-queen-starter-kit');
  }

  #getCompareLinePrice(item) {
    if (this.#usesCartComparePrice(item)) {
      return item.original_line_price > item.final_line_price ? item.original_line_price : null;
    }

    const compareAt = this.variantCompareAtPrices.get(String(item.variant_id));
    if (!compareAt) return null;

    const compareLine = compareAt * item.quantity;
    return compareLine > item.final_line_price ? compareLine : null;
  }

  #getSubtotalCompareTotal(drawerItems) {
    const subtotal = drawerItems.reduce((sum, entry) => sum + this.#getEntryLinePrice(entry), 0);
    const compareTotal = drawerItems.reduce((sum, entry) => {
      const comparePrice = this.#getEntryComparePrice(entry);
      return sum + (comparePrice ?? this.#getEntryLinePrice(entry));
    }, 0);

    return compareTotal > subtotal ? compareTotal : null;
  }

  #getPlanDurationScore(plan) {
    if (!plan) return 0;
    if (plan.durationScore) return plan.durationScore;

    const sources = [plan.name, plan.title, plan.subtitle].filter(Boolean).join(' ');
    const lower = sources.toLowerCase();

    const monthMatch = lower.match(/(\d+)\s*[- ]?\s*month/);
    if (monthMatch) return Number(monthMatch[1]) * 30;

    const weekMatch = lower.match(/(\d+)\s*[- ]?\s*week/);
    if (weekMatch) return Number(weekMatch[1]) * 7;

    const dayMatch = lower.match(/(\d+)\s*[- ]?\s*day/);
    if (dayMatch) return Number(dayMatch[1]);

    if (lower.includes('annual') || lower.includes('year')) return 365;
    if (lower.includes('monthly') || lower.includes('1 month')) return 30;

    return 0;
  }

  #getBestPlan(plans) {
    if (!plans?.length) return null;

    const ranked = [...plans].sort((a, b) => {
      const durationDiff = this.#getPlanDurationScore(b) - this.#getPlanDurationScore(a);
      if (durationDiff !== 0) return durationDiff;
      return (b.price || 0) - (a.price || 0);
    });

    return ranked[0];
  }

  #getLowestPlan(plans) {
    if (!plans?.length) return null;

    const ranked = [...plans].sort((a, b) => {
      const durationDiff = this.#getPlanDurationScore(a) - this.#getPlanDurationScore(b);
      if (durationDiff !== 0) return durationDiff;
      return (a.price || 0) - (b.price || 0);
    });

    return ranked[0];
  }

  #getPlanSelectorPlans(plans) {
    if (!plans?.length) return [];

    const lowestPlan = this.#getLowestPlan(plans);
    const bestPlan = this.#getBestPlan(plans);
    const selectorPlans = [];

    if (lowestPlan) selectorPlans.push(lowestPlan);

    const lowestPlanId = Number(lowestPlan?.sellingPlanId);
    const bestPlanId = Number(bestPlan?.sellingPlanId);
    if (bestPlan && bestPlanId !== lowestPlanId) selectorPlans.push(bestPlan);

    return selectorPlans;
  }

  #withLowestSubscriptionPlan(rec) {
    if (!rec) return null;

    const family = this.config.families?.find((entry) => entry.id === rec.familyId);
    const lowestPlan = this.#getLowestPlan(family?.plans);
    if (!lowestPlan) return rec;

    return {
      ...rec,
      variantId: lowestPlan.variantId,
      sellingPlanId: lowestPlan.sellingPlanId,
      subtitle: lowestPlan.subtitle || rec.subtitle,
      price: lowestPlan.price ? formatMoney(lowestPlan.price) : rec.price,
    };
  }

  #getAggregateGiftState(drawerItems) {
    if (!drawerItems.length) return 'empty';
    if (drawerItems.some((entry) => entry.isBest)) return 'activated';
    if (drawerItems.some((entry) => entry.isSubscription)) return 'not-activated';
    return 'one-time';
  }

  #renderRewardBar(hasItems, aggregateState, drawerItems, cart) {
    if (!this.rewardBarEl) return;

    const strings = this.config.strings || {};
    let message = strings.reward_empty || '';

    if (hasItems) {
      if (aggregateState === 'activated') {
        message = strings.reward_unlocked || '';
      } else {
        message = strings.reward_almost || '';
      }
    }

    const messageEl = this.rewardBarEl.querySelector('.js-pos-cart-drawer-reward-message');
    if (messageEl) messageEl.textContent = message;

    const steps = this.rewardBarEl.querySelectorAll('.js-pos-cart-drawer-reward-step');
    for (const step of steps) {
      const isActive = this.#isRewardStepActive(step.dataset.step, hasItems, drawerItems, cart);
      step.classList.toggle('pos-cart-drawer__reward-step--active', isActive);
      step.classList.toggle('pos-cart-drawer__reward-step--inactive', !isActive);
    }

    const nodes = this.rewardBarEl.querySelectorAll('.js-pos-cart-drawer-reward-node');
    let filledNodes = 0;

    for (const node of nodes) {
      const isFilled = this.#isRewardStepActive(node.dataset.rewardStep, hasItems, drawerItems, cart);
      node.classList.toggle('pos-cart-drawer__reward-node--filled', isFilled);
      if (isFilled) filledNodes += 1;
    }

    // Derived from the nodes that actually lit, so the fill stays correct for
    // whichever step set the template rendered.
    const progress = nodes.length ? Math.round((filledNodes / nodes.length) * 100) : 0;
    const fillEl = this.rewardBarEl.querySelector('.js-pos-cart-drawer-reward-progress-fill');
    if (fillEl) fillEl.style.width = `${progress}%`;
  }

  #isRewardStepActive(stepKey, hasItems, drawerItems, cart) {
    if (!hasItems) return false;
    if (stepKey === 'shipping') return true;
    if (stepKey === 'gifts') return drawerItems.some((entry) => entry.isSubscription);
    if (stepKey === 'goddess_gift') return Boolean(this.#findFreeGiftLine(cart));
    if (stepKey === 'bag') return drawerItems.some((entry) => entry.isBest);
    return false;
  }

  #renderBody(cart, drawerItems, hasItems, aggregateState) {
    if (!this.bodyEl) return;

    if (!hasItems) {
      this.bodyEl.innerHTML = this.#renderEmptyState();
      return;
    }

    const parts = drawerItems.map((entry) => this.#renderCartItem(entry)).join('');
    const freeGift = this.#renderFreeGiftRow(cart);
    const moreProducts = this.#renderMoreProducts(cart, drawerItems);

    this.bodyEl.innerHTML = `${parts}${freeGift}${moreProducts}`;
  }

  #renderFreeGiftRow(cart) {
    const gift = this.config.freeGift;
    const giftLine = gift?.variantId ? this.#findFreeGiftLine(cart) : null;
    if (!giftLine) return '';

    const title = gift.title || giftLine.product_title;
    const image = gift.image || giftLine.image;
    const note = gift.note
      ? `<p class="pos-cart-drawer__gift-line-note">${escapeHtml(gift.note)}</p>`
      : '';

    return `
      <article class="pos-cart-drawer__card pos-cart-drawer__gift-line">
        <div class="pos-cart-drawer__card-media">
          ${
            image
              ? `<img class="pos-cart-drawer__card-image" src="${getSizedImageUrl(image, '180x')}" alt="${escapeHtml(title)}" loading="lazy" width="90" height="90">`
              : ''
          }
        </div>
        <div class="pos-cart-drawer__gift-line-info">
          <h3 class="pos-cart-drawer__card-title">${escapeHtml(title)}</h3>
          ${note}
        </div>
        <span class="pos-cart-drawer__gift-line-badge">${escapeHtml(gift.badge || 'FREE')}</span>
      </article>
    `;
  }

  #renderEmptyState() {
    const strings = this.config.strings || {};
    const recommendations = (this.config.recommendations || [])
      .map((rec) => this.#withLowestSubscriptionPlan(rec))
      .filter(Boolean);

    const recHtml = recommendations.length
      ? `
        <section class="pos-cart-drawer__ritual" aria-label="${escapeHtml(strings.ritual_heading || '')}">
          <h3 class="pos-cart-drawer__ritual-heading">${escapeHtml(strings.ritual_heading || '')}</h3>
          <div class="pos-cart-drawer__ritual-grid">
            ${recommendations.map((rec) => this.#renderRecommendationCard(rec)).join('')}
          </div>
        </section>
      `
      : '';

    return `
      <div class="pos-cart-drawer__empty-state">
        <div class="pos-cart-drawer__empty-copy">
          <h3 class="pos-cart-drawer__empty-title">${escapeHtml(strings.empty_title || '')}</h3>
          <p class="pos-cart-drawer__empty-text">${escapeHtml(strings.empty_text || '')}</p>
          <button type="button" class="pos-cart-drawer__btn pos-cart-drawer__btn--primary js-pos-cart-drawer-shop-now">
            <span>${escapeHtml(strings.shop_now || '')}</span>
            ${this.#crownIcon()}
          </button>
        </div>
        ${recHtml}
      </div>
    `;
  }

  #renderCartItem(entry) {
    if (entry.children.length) return this.#renderBundleItem(entry);

    const { item, family, currentPlan, bestPlan, isBest, isSubscription } = entry;
    const strings = this.config.strings || {};
    const displayTitle = trimParentheticalTitle(item.product_title);
    const compareLinePrice = this.#getCompareLinePrice(item);
    const comparePrice = compareLinePrice
      ? `<span class="pos-cart-drawer__price-compare">${formatMoney(compareLinePrice)}</span>`
      : '';

    const subscriptionNote = currentPlan?.subscriptionNote
      ? `
        <p class="pos-cart-drawer__subscription-note">
          ${this.#clockIcon()}
          <span>${escapeHtml(currentPlan.subscriptionNote)}</span>
        </p>
      `
      : '';

    const planSelector = isSubscription && family
      ? this.#renderPlanSelector(family, currentPlan, bestPlan, item, strings)
      : isSubscription
        ? `<p class="pos-cart-drawer__card-subtitle">${escapeHtml(item.selling_plan_allocation?.selling_plan?.name || '')}</p>`
        : '';

    return `
      <article class="pos-cart-drawer__card" data-line-key="${escapeHtml(item.key)}" data-gift-state="${isBest ? 'activated' : 'not-activated'}">
        <div class="pos-cart-drawer__card-main">
          <div class="pos-cart-drawer__card-main-content">
            <div class="pos-cart-drawer__card-media">
              ${
                item.image
                  ? `<img class="pos-cart-drawer__card-image" src="${getSizedImageUrl(item.image, '180x')}" alt="${escapeHtml(displayTitle)}" loading="lazy" width="90" height="90">`
                  : ''
              }
            </div>
            <div class="pos-cart-drawer__card-info">
              <div class="pos-cart-drawer__card-info-main">
                <div class="pos-cart-drawer__card-top">
                  <div class="pos-cart-drawer__card-titles">
                    <div class="pos-cart-drawer__card-title-container">
                    <h3 class="pos-cart-drawer__card-title">${escapeHtml(displayTitle)}</h3>
                    <button type="button" class="pos-cart-drawer__remove js-pos-cart-drawer-remove" data-line="${escapeHtml(item.key)}" aria-label="${escapeHtml(strings.remove_label || 'Remove')}">
                    <span class="pos-cart-drawer__remove-spinner" aria-hidden="true"></span>
                    <span class="pos-cart-drawer__remove-content">${escapeHtml(strings.remove_label || 'Remove')}</span>
                    </button>
                    </div>
                    ${planSelector}
                  </div>
                </div>
                <div class="pos-cart-drawer__card-actions">
                  <div class="pos-cart-drawer__qty" role="group" aria-label="${escapeHtml(strings.quantity_label || 'Quantity')}">
                    <span class="pos-cart-drawer__qty-spinner" aria-hidden="true"></span>
                    <button type="button" class="pos-cart-drawer__qty-btn js-pos-cart-drawer-qty" data-line="${escapeHtml(item.key)}" data-quantity="${item.quantity - 1}" aria-label="${escapeHtml(strings.decrease_qty || 'Decrease quantity')}">−</button>
                    <span class="pos-cart-drawer__qty-value" aria-live="polite">${item.quantity}</span>
                    <button type="button" class="pos-cart-drawer__qty-btn js-pos-cart-drawer-qty" data-line="${escapeHtml(item.key)}" data-quantity="${item.quantity + 1}" aria-label="${escapeHtml(strings.increase_qty || 'Increase quantity')}">+</button>
                  </div>
                  <div class="pos-cart-drawer__card-prices">
                    ${comparePrice}
                    <span class="pos-cart-drawer__price">${formatMoney(item.final_line_price)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          ${subscriptionNote}
        </div>
      </article>
    `;
  }

  // A bundle renders as one card: the parent names the set and the price is the
  // components' sum.
  //
  // Its stepper counts sets, not lines. A change there has to move the parent
  // and every product together — applied to the parent alone it would leave one
  // set's worth of products under a parent claiming two — so each component's
  // per-set count rides on the buttons for the handler to scale by.
  #renderBundleItem(entry) {
    const { item, children, isBest, isSubscription, bundleFamily, bundlePlan, bundleBestPlan } =
      entry;
    const strings = this.config.strings || {};
    const displayTitle = trimParentheticalTitle(item.product_title);

    const linePrice = this.#getEntryLinePrice(entry);
    const comparePrice = this.#getEntryComparePrice(entry);
    const compareHtml = comparePrice
      ? `<span class="pos-cart-drawer__price-compare">${formatMoney(comparePrice)}</span>`
      : '';

    // The parent is a $0 container and often has no artwork of its own, so it
    // borrows the first component's image rather than rendering an empty well.
    const image = item.image || children.find((child) => child.item.image)?.item.image;

    // The parent carries no plan of its own — each product subscribes on its
    // own plan — so the cadence is read off the components. They are all on the
    // same supply, having been added by one button, so the first one speaks for
    // the set. A parent that does carry a plan is still believed first.
    const planName =
      item.selling_plan_allocation?.selling_plan?.name ||
      children.find((child) => child.isSubscription)?.item.selling_plan_allocation?.selling_plan
        ?.name;
    const planLabel = isSubscription ? this.#getPlanLabel({ name: planName }, strings) : '';

    // A subscription bundle this page defines gets the same controls a product
    // card gets — the cadence dropdown and the switch to the best plan — in
    // place of the read-only component list, which said what the set holds but
    // offered nothing to do about it.
    //
    // The list stays wherever there is nothing to put there instead: a one-time
    // set, a single supply, or a bundle whose options this page does not carry.
    const canSwitchPlans = Boolean(
      isSubscription && bundleFamily && this.#getPlanSelectorPlans(bundleFamily.plans).length > 1
    );

    const planHtml = canSwitchPlans
      ? this.#renderPlanSelector(bundleFamily, bundlePlan, bundleBestPlan, item, strings)
      : planLabel
        ? `<p class="pos-cart-drawer__card-subtitle">${escapeHtml(planLabel)}</p>`
        : '';

    const subscriptionNote = bundlePlan?.subscriptionNote
      ? `
        <p class="pos-cart-drawer__subscription-note">
          ${this.#clockIcon()}
          <span>${escapeHtml(bundlePlan.subscriptionNote)}</span>
        </p>
      `
      : '';

    const includes = canSwitchPlans
      ? ''
      : children
          .map(
            (child) =>
              `<li class="pos-cart-drawer__bundle-item">${escapeHtml(
                trimParentheticalTitle(child.item.product_title)
              )}</li>`
          )
          .join('');

    // Every component key rides on the remove button so one click can zero the
    // whole group in a single request.
    const childKeys = children.map((child) => child.item.key).join(',');

    // Adding the same bundle twice merges the parent and every component onto
    // their existing lines, so the parent's quantity is the number of sets in
    // the cart and each component holds that many of its own per-set count.
    // Those counts ride on the buttons so a change can move the whole group in
    // one request; stepping down from one zeroes the set, as removing it does.
    const bundleUnits = children
      .map((child) => this.#getBundleUnitQuantity(child.item, item.quantity))
      .join(',');

    const quantityHtml = `
      <div class="pos-cart-drawer__qty" role="group" aria-label="${escapeHtml(strings.quantity_label || 'Quantity')}">
        <span class="pos-cart-drawer__qty-spinner" aria-hidden="true"></span>
        <button type="button" class="pos-cart-drawer__qty-btn js-pos-cart-drawer-qty" data-line="${escapeHtml(item.key)}" data-quantity="${item.quantity - 1}" data-bundle-lines="${escapeHtml(childKeys)}" data-bundle-units="${escapeHtml(bundleUnits)}" aria-label="${escapeHtml(strings.decrease_qty || 'Decrease quantity')}">−</button>
        <span class="pos-cart-drawer__qty-value" aria-live="polite">${item.quantity}</span>
        <button type="button" class="pos-cart-drawer__qty-btn js-pos-cart-drawer-qty" data-line="${escapeHtml(item.key)}" data-quantity="${item.quantity + 1}" data-bundle-lines="${escapeHtml(childKeys)}" data-bundle-units="${escapeHtml(bundleUnits)}" aria-label="${escapeHtml(strings.increase_qty || 'Increase quantity')}">+</button>
      </div>
    `;

    return `
      <article class="pos-cart-drawer__card pos-cart-drawer__card--bundle" data-line-key="${escapeHtml(item.key)}" data-gift-state="${isBest ? 'activated' : 'not-activated'}">
        <div class="pos-cart-drawer__card-main">
          <div class="pos-cart-drawer__card-main-content">
            <div class="pos-cart-drawer__card-media">
              ${
                image
                  ? `<img class="pos-cart-drawer__card-image" src="${getSizedImageUrl(image, '180x')}" alt="${escapeHtml(displayTitle)}" loading="lazy" width="90" height="90">`
                  : ''
              }
            </div>
            <div class="pos-cart-drawer__card-info">
              <div class="pos-cart-drawer__card-info-main">
                <div class="pos-cart-drawer__card-top">
                  <div class="pos-cart-drawer__card-titles">
                    <div class="pos-cart-drawer__card-title-container">
                      <h3 class="pos-cart-drawer__card-title">${escapeHtml(displayTitle)}</h3>
                      <button type="button" class="pos-cart-drawer__remove js-pos-cart-drawer-remove" data-line="${escapeHtml(item.key)}" data-bundle-lines="${escapeHtml(childKeys)}" aria-label="${escapeHtml(strings.remove_label || 'Remove')}">
                        <span class="pos-cart-drawer__remove-spinner" aria-hidden="true"></span>
                        <span class="pos-cart-drawer__remove-content">${escapeHtml(strings.remove_label || 'Remove')}</span>
                      </button>
                    </div>
                    ${planHtml}
                  </div>
                </div>
                ${includes ? `<ul class="pos-cart-drawer__bundle-list">${includes}</ul>` : ''}
                <div class="pos-cart-drawer__card-actions">
                  ${quantityHtml}
                  <div class="pos-cart-drawer__card-prices">
                    ${compareHtml}
                    <span class="pos-cart-drawer__price">${formatMoney(linePrice)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          ${subscriptionNote}
        </div>
      </article>
    `;
  }

  // A component line holds one set's worth for every set in the cart, so what
  // the group scales by is the per-set count. A parent at zero — or a component
  // that does not divide evenly into it, which no ordinary add can produce —
  // falls back to the line's own quantity rather than inventing a fraction.
  #getBundleUnitQuantity(childItem, parentQuantity) {
    if (!(parentQuantity > 0)) return childItem.quantity;

    const unit = Math.round(childItem.quantity / parentQuantity);
    return unit > 0 ? unit : childItem.quantity;
  }

  #getPlanLabel(plan, strings) {
    if (!plan) return '';

    const score = this.#getPlanDurationScore(plan);
    if (score >= 90) return strings.plan_ships_3_month || 'Ships every 3 months';
    if (score >= 30) return strings.plan_ships_1_month || 'Ships every 1 month';

    return plan.subtitle || plan.title || plan.name || '';
  }

  #renderPlanSelector(family, currentPlan, bestPlan, item, strings) {
    const plans = family?.plans?.filter(Boolean) || [];
    const selectorPlans = this.#getPlanSelectorPlans(plans);

    if (selectorPlans.length < 2) {
      const label = this.#getPlanLabel(currentPlan, strings);
      return label ? `<p class="pos-cart-drawer__card-subtitle">${escapeHtml(label)}</p>` : '';
    }

    const currentPlanId = Number(currentPlan?.sellingPlanId);
    const bestPlanId = Number(bestPlan?.sellingPlanId);
    const currentLabel = this.#getPlanLabel(currentPlan, strings);
    const menuPlans = [...selectorPlans].sort((a, b) => {
      const aSelected = Number(a.sellingPlanId) === currentPlanId;
      const bSelected = Number(b.sellingPlanId) === currentPlanId;
      if (aSelected !== bSelected) return aSelected ? -1 : 1;
      return this.#getPlanDurationScore(a) - this.#getPlanDurationScore(b);
    });

    const options = menuPlans
      .map((plan) => {
        const planId = Number(plan.sellingPlanId);
        const isSelected = planId === currentPlanId;
        const isBest = planId === bestPlanId;
        const label = this.#getPlanLabel(plan, strings);
        const badge = isBest && !isSelected
          ? `<span class="pos-cart-drawer__plan-option-badge">${escapeHtml(strings.upgrade_badge || 'Best Value')}</span>`
          : '';
        const check = isSelected
          ? `<span class="pos-cart-drawer__plan-option-check">${this.#checkIcon()}</span>`
          : '';

        return `
          <button
            type="button"
            class="pos-cart-drawer__plan-option js-pos-cart-drawer-plan-option${isSelected ? ' pos-cart-drawer__plan-option--selected' : ''}"
            role="option"
            aria-selected="${isSelected ? 'true' : 'false'}"
            data-line="${escapeHtml(item.key)}"
            data-family-id="${escapeHtml(family.id)}"
            data-plan-id="${planId}"
          >
            <span class="pos-cart-drawer__plan-option-label">${escapeHtml(label)}</span>
            ${badge}
            ${check}
          </button>
        `;
      })
      .join('');

    // The unlocked notice names a savings percentage, and the one it would
    // print for a bundle is the single product's. Held back until the set's own
    // discount is settled — a card on the best plan simply shows no notice.
    const isOnBestPlan = currentPlanId === bestPlanId;
    const planAction = isOnBestPlan
      ? family.isBundle
        ? ''
        : this.#renderPlanUnlockedNotice(family, item, strings)
      : bestPlan
        ? `
          <button
            type="button"
            class="pos-cart-drawer__plan-switch js-pos-cart-drawer-plan-switch"
            data-line="${escapeHtml(item.key)}"
            data-family-id="${escapeHtml(family.id)}"
            data-plan-id="${bestPlanId}"
          >
            <span class="pos-cart-drawer__plan-switch-label">${escapeHtml(strings.plan_switch_free_bag || 'Get My FREE Bag')}
            <span class="pos-cart-drawer__plan-switch-arrow" aria-hidden="true">→</span>
            </span>
          </button>
        `
        : '';

    return `
      <div class="pos-cart-drawer__plan-controls" data-plan-select>
        <div class="pos-cart-drawer__plan-select">
          <button
            type="button"
            class="pos-cart-drawer__plan-trigger js-pos-cart-drawer-plan-trigger"
            aria-expanded="false"
            aria-haspopup="listbox"
          >
            <span class="pos-cart-drawer__plan-trigger-label">${escapeHtml(currentLabel)}</span>
            <span class="pos-cart-drawer__plan-trigger-chevron">${this.#chevronIcon()}</span>
            <span class="pos-cart-drawer__plan-spinner" aria-hidden="true"></span>
          </button>
          <div class="pos-cart-drawer__plan-menu" role="listbox" hidden>
            ${options}
          </div>
        </div>
        ${planAction}
      </div>
    `;
  }

  #formatPlanUnlockedMessage(strings, family, item) {
    const template = strings.plan_unlocked_message || '%save% OFF + FREE 3rd Bag Unlocked!';
    const savingsPercent = this.#getUpgradeSavePercent(family, item);
    const saveLabel = savingsPercent > 0 ? `${savingsPercent}%` : '';

    return template.replace('%save%', saveLabel).replace(/\s{2,}/g, ' ').trim();
  }

  #renderPlanUnlockedNotice(family, item, strings) {
    const message = this.#formatPlanUnlockedMessage(strings, family, item);
    if (!message) return '';

    return `
      <div class="pos-cart-drawer__plan-unlocked" role="status">
        <span class="pos-cart-drawer__plan-unlocked-icon" aria-hidden="true">
          ${this.#giftIcon()}
        </span>
        <p class="pos-cart-drawer__plan-unlocked-text">${escapeHtml(message)}</p>
      </div>
    `;
  }

  #togglePlanSelect(wrapper) {
    if (!wrapper) return;

    const isOpen = wrapper.classList.contains('pos-cart-drawer__plan-controls--open');
    this.#closeAllPlanSelects();

    if (isOpen) return;

    wrapper.classList.add('pos-cart-drawer__plan-controls--open');
    const trigger = wrapper.querySelector('.js-pos-cart-drawer-plan-trigger');
    const menu = wrapper.querySelector('.pos-cart-drawer__plan-menu');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    if (menu) menu.hidden = false;
  }

  #closeAllPlanSelects() {
    for (const wrapper of this.root.querySelectorAll('[data-plan-select]')) {
      wrapper.classList.remove('pos-cart-drawer__plan-controls--open');
      const trigger = wrapper.querySelector('.js-pos-cart-drawer-plan-trigger');
      const menu = wrapper.querySelector('.pos-cart-drawer__plan-menu');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (menu) menu.hidden = true;
    }
  }

  #setPlanSelectLoading(wrapper, isLoading) {
    if (!wrapper) return;

    wrapper.classList.toggle('pos-cart-drawer__plan-controls--loading', isLoading);
    if (isLoading) this.#closeAllPlanSelects();
  }

  #getUpgradeSavePercent() {
    return 33;
  }

  #formatUpgradeTitle(template, bestPlan, currentPlan, strings, family, item) {
    const titleTemplate = template || strings.upgrade_title_default || 'Upgrade to %plan% & Save %save%';
    const planName = bestPlan?.title || bestPlan?.name || '';
    const savingsPercent = this.#getUpgradeSavePercent(family, item);
    const saveLabel = savingsPercent > 0 ? `${savingsPercent}%` : '';

    let title = titleTemplate.replace('%plan%', planName);

    if (titleTemplate.includes('%save%')) {
      title = title.replace('%save%', saveLabel);
      if (!saveLabel) title = title.replace(/\s*&?\s*Save\s*$/i, '').trim();
    } else if (saveLabel) {
      title = `${title} ${saveLabel}`.trim();
    }

    return title.replace(/\s{2,}/g, ' ').trim();
  }

  #renderUpgradeBlock(family, bestPlan, currentPlan, item) {
    if (!bestPlan) return '';

    const strings = this.config.strings || {};
    const upgrade = family.upgrade || {};
    const title = this.#formatUpgradeTitle(upgrade.title, bestPlan, currentPlan, strings, family, item);

    const bullets = (upgrade.bullets?.length ? upgrade.bullets : strings.upgrade_bullets_default || [])
      .map(
        (bullet) => `
          <li class="pos-cart-drawer__upgrade-benefit">
            ${this.#checkIcon()}
            <span>${formatBulletHtml(bullet)}</span>
          </li>
        `
      )
      .join('');

    return `
      <div class="pos-cart-drawer__upgrade-wrap">
        <div class="pos-cart-drawer__upgrade">
          <span class="pos-cart-drawer__upgrade-badge">${escapeHtml(upgrade.badge || strings.upgrade_badge || 'Best Value')}</span>
          <div class="pos-cart-drawer__upgrade-inner">
            <h4 class="pos-cart-drawer__upgrade-title">${escapeHtml(title)}</h4>
            <ul class="pos-cart-drawer__upgrade-list">${bullets}</ul>
            <button
              type="button"
              class="pos-cart-drawer__btn pos-cart-drawer__btn--outline js-pos-cart-drawer-upgrade"
              data-line="${escapeHtml(item.key)}"
              data-family-id="${escapeHtml(family.id)}"
            >
              <span class="pos-cart-drawer__btn-spinner" aria-hidden="true"></span>
              <span class="pos-cart-drawer__btn-content">
                <span>${escapeHtml(upgrade.cta || strings.upgrade_cta || 'Upgrade & Save Now')}</span>
                ${this.#crownIcon()}
              </span>
            </button>
          </div>
        </div>
      </div>
    `;
  }

  #renderMoreProducts(cart, drawerItems) {
    if (cart?.item_count > 0 || cart?.items?.length > 0 || drawerItems?.length > 0) {
      return '';
    }

    const strings = this.config.strings || {};
    const recommendations = this.#getMoreProductRecommendations(cart, drawerItems);

    if (!recommendations.length) return '';

    return `
      <section class="pos-cart-drawer__more" aria-label="${escapeHtml(strings.more_heading || '')}">
        <h3 class="pos-cart-drawer__more-heading">${escapeHtml(strings.more_heading || '')}</h3>
        <div class="pos-cart-drawer__more-grid">
          ${recommendations.map((rec) => this.#renderRecommendationCard(rec, true)).join('')}
        </div>
      </section>
    `;
  }

  #getMoreProductRecommendations(cart, drawerItems) {
    const inCartProductIds = new Set(cart.items?.map((item) => item.product_id) || []);
    // A product nested inside a bundle is still in the cart, so its family has
    // to count here the same as one sitting at the top level.
    const familyIdsInCart = new Set(
      drawerItems.flatMap((entry) => [entry, ...entry.children]).map((entry) => entry.family?.id)
    );

    return (this.config.recommendations || [])
      .map((rec) => this.#withLowestSubscriptionPlan(rec))
      .filter((rec) => {
        if (!rec) return false;
        if (inCartProductIds.has(rec.productId)) return false;
        if (rec.familyId && familyIdsInCart.has(rec.familyId)) return false;
        return true;
      });
  }

  #renderRecommendationCard(rec, compact = false) {
    const modifier = compact ? ' pos-cart-drawer__rec-card--compact' : '';

    return `
      <article class="pos-cart-drawer__rec-card${modifier}">
        ${
          rec.image
            ? `<img class="pos-cart-drawer__rec-image" src="${escapeHtml(rec.image)}" alt="${escapeHtml(rec.title || '')}" loading="lazy" width="160" height="160">`
            : ''
        }
        <div class="pos-cart-drawer__rec-info">
          <div class="pos-cart-drawer__rec-info-inner">
          <h4 class="pos-cart-drawer__rec-title">${escapeHtml(rec.title || '')}</h4>
          <p class="pos-cart-drawer__rec-subtitle">${escapeHtml(rec.subtitle || '')}</p>
          </div>
          <p class="pos-cart-drawer__rec-price">${escapeHtml(rec.price || '')}</p>
        </div>
        <button
          type="button"
          class="pos-cart-drawer__btn pos-cart-drawer__btn--outline js-pos-cart-drawer-add-rec"
          data-variant-id="${escapeHtml(rec.variantId)}"
          data-selling-plan-id="${escapeHtml(rec.sellingPlanId || '')}"
        >
          <span class="pos-cart-drawer__btn-spinner" aria-hidden="true"></span>
          <span class="pos-cart-drawer__btn-content">
            <span>${escapeHtml(rec.cta || 'Add to Cart')}</span>
            ${this.#crownIcon('pos-cart-drawer__crown--green')}
          </span>
        </button>
      </article>
    `;
  }

  #formatSubtotalLabel(count, strings) {
    const singular = strings.subtotal_item_singular || 'item';
    const plural = strings.subtotal_item_plural || 'items';
    const itemWord = count === 1 ? singular : plural;
    let template = strings.subtotal || 'Subtotal (%count% %items%)';

    template = template.replace(/%items%/g, itemWord);
    template = template.replace(/%count%/g, String(count));

    if (count === 1) {
      template = template.replace(/\bitems\b/gi, singular);
    }

    return template;
  }

  #renderFooter(cart, drawerItems, hasItems) {
    if (!this.footerEl) return;

    this.footerEl.hidden = !hasItems;

    if (!hasItems) return;

    const strings = this.config.strings || {};
    const count = drawerItems.reduce((sum, entry) => sum + entry.item.quantity, 0);
    const subtotal = drawerItems.reduce((sum, entry) => sum + this.#getEntryLinePrice(entry), 0);
    const subtotalCompare = this.#getSubtotalCompareTotal(drawerItems);

    if (this.subtotalEl) {
      this.subtotalEl.textContent = this.#formatSubtotalLabel(count, strings);
    }

    if (this.subtotalCompareEl) {
      if (subtotalCompare) {
        this.subtotalCompareEl.hidden = false;
        this.subtotalCompareEl.textContent = formatMoney(subtotalCompare);
      } else {
        this.subtotalCompareEl.hidden = true;
        this.subtotalCompareEl.textContent = '';
      }
    }

    if (this.subtotalPriceEl) {
      this.subtotalPriceEl.textContent = formatMoney(subtotal);
    }

    if (this.shippingPriceEl) {
      this.shippingPriceEl.textContent = strings.shipping_free || 'FREE';
    }

    if (this.checkoutLabelEl) {
      this.checkoutLabelEl.textContent = strings.checkout || 'PROCEED TO CHECKOUT';
    }
  }

  async #removeItem(lineKey, button) {
    this.#cancelQtyPending(lineKey);
    this.#setRemoveButtonLoading(button, true);

    try {
      // Removing a bundle takes its components with it. They are not separately
      // purchasable, so zeroing the parent alone would strand six products in
      // the cart with nothing left to group them.
      const nestedKeys = button?.dataset.bundleLines;

      if (nestedKeys) {
        await this.#removeLines([lineKey, ...nestedKeys.split(',')]);
      } else {
        await this.#syncQuantity(lineKey, 0);
      }
    } finally {
      this.#setRemoveButtonLoading(button, false);
    }
  }

  async #removeLines(lineKeys) {
    const updates = {};
    for (const key of lineKeys) {
      if (key) updates[key] = 0;
    }

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });

      this.#patchCart(await this.#reconcileFreeGift(await response.json()));
    } catch (error) {
      console.error(error);
    }
  }

  #bundleMatchesDom(entry) {
    const card = this.#findCartCard(entry.item.key);
    if (!card) return false;

    const rendered = card.querySelector('.js-pos-cart-drawer-remove')?.dataset.bundleLines || '';
    return rendered === entry.children.map((child) => child.item.key).join(',');
  }

  #findCartCard(lineKey) {
    if (!this.bodyEl || !lineKey) return null;
    return this.bodyEl.querySelector(`.pos-cart-drawer__card[data-line-key="${CSS.escape(lineKey)}"]`);
  }

  #getLineQuantityFromDom(lineKey) {
    const card = this.#findCartCard(lineKey);
    const qtyValue = card?.querySelector('.pos-cart-drawer__qty-value');
    if (!qtyValue) return null;
    const parsed = Number(qtyValue.textContent);
    return Number.isFinite(parsed) ? parsed : null;
  }

  #patchLineQuantity(lineKey, quantity) {
    const card = this.#findCartCard(lineKey);
    if (!card || quantity < 1) return;

    const qtyValue = card.querySelector('.pos-cart-drawer__qty-value');
    if (qtyValue) qtyValue.textContent = String(quantity);

    const qtyButtons = card.querySelectorAll('.js-pos-cart-drawer-qty');
    if (qtyButtons[0]) qtyButtons[0].dataset.quantity = String(quantity - 1);
    if (qtyButtons[1]) qtyButtons[1].dataset.quantity = String(quantity + 1);
  }

  #patchCartItemPrices(card, linePrice, compareLinePrice) {
    const priceEl = card.querySelector('.pos-cart-drawer__price');
    if (priceEl) priceEl.textContent = formatMoney(linePrice);

    const pricesEl = card.querySelector('.pos-cart-drawer__card-prices');
    if (!pricesEl) return;

    let compareEl = pricesEl.querySelector('.pos-cart-drawer__price-compare');

    if (compareLinePrice) {
      if (!compareEl) {
        compareEl = document.createElement('span');
        compareEl.className = 'pos-cart-drawer__price-compare';
        pricesEl.insertBefore(compareEl, priceEl);
      }
      compareEl.textContent = formatMoney(compareLinePrice);
      return;
    }

    compareEl?.remove();
  }

  #patchCartItem(entry) {
    const { item } = entry;
    const card = this.#findCartCard(item.key);
    if (!card) return false;

    // A bundle card shows the group's summed price, not the $0 its parent line
    // carries, so both figures come from the entry rather than the line.
    this.#patchLineQuantity(item.key, item.quantity);
    this.#patchCartItemPrices(
      card,
      this.#getEntryLinePrice(entry),
      this.#getEntryComparePrice(entry)
    );
    return true;
  }

  #patchCart(cart) {
    const drawerItems = this.#getDrawerItems(cart.items);
    const hasItems = drawerItems.length > 0;

    if (!hasItems) {
      this.#render(cart);
      return;
    }

    const domCardCount = this.bodyEl?.querySelectorAll('.pos-cart-drawer__card[data-line-key]').length ?? 0;
    if (domCardCount !== drawerItems.length) {
      this.#render(cart);
      return;
    }

    // The gift line is added and removed behind the customer's back, so a patch
    // can't assume the row it left in the DOM still matches the cart.
    const giftRowInDom = Boolean(this.bodyEl?.querySelector('.pos-cart-drawer__gift-line'));
    if (giftRowInDom !== Boolean(this.#findFreeGiftLine(cart))) {
      this.#render(cart);
      return;
    }

    // A patch only touches quantities and prices, so a bundle whose components
    // have changed underneath it needs the full render that redraws its list.
    if (drawerItems.some((entry) => !this.#bundleMatchesDom(entry))) {
      this.#render(cart);
      return;
    }

    this.#updateCounters(cart);

    const aggregateState = this.#getAggregateGiftState(drawerItems);
    const previousGiftState = this.root.dataset.giftState;

    this.root.classList.remove('pos-cart-drawer--empty');
    this.root.dataset.giftState = aggregateState;

    if (previousGiftState !== aggregateState) {
      this.#renderRewardBar(hasItems, aggregateState, drawerItems, cart);
    }

    for (const entry of drawerItems) {
      if (!this.#patchCartItem(entry)) {
        this.#render(cart);
        return;
      }
    }

    this.#renderFooter(cart, drawerItems, hasItems);
  }

  #queueQuantityChange(lineKey, quantity, button) {
    if (quantity < 0 || !lineKey) return;

    const card = this.#findCartCard(lineKey);
    if (!card) return;

    const qtyEl = card.querySelector('.pos-cart-drawer__qty');
    if (qtyEl?.classList.contains('pos-cart-drawer__qty--loading')) return;

    const existing = this.qtyPending.get(lineKey);
    if (existing?.timer) clearTimeout(existing.timer);

    const baseline = existing?.baseline ?? this.#getLineQuantityFromDom(lineKey);
    const bundleLines = this.#readBundleLines(button) ?? existing?.bundleLines ?? null;

    if (quantity > 0) {
      this.#patchLineQuantity(lineKey, quantity);
    }

    const timer = setTimeout(() => {
      this.#flushQuantityChange(lineKey);
    }, POS_CART_QTY_DEBOUNCE_MS);

    this.qtyPending.set(lineKey, { quantity, baseline, timer, card, bundleLines });
  }

  // The stepper on a bundle card carries its components: each key with the
  // number of it one set holds. A stepper without them belongs to an ordinary
  // line and moves nothing but itself.
  #readBundleLines(button) {
    const keys = (button?.dataset.bundleLines || '').split(',').filter(Boolean);
    if (!keys.length) return null;

    const units = (button.dataset.bundleUnits || '').split(',');

    return keys.map((key, index) => ({ key, unit: Number(units[index]) || 1 }));
  }

  async #flushQuantityChange(lineKey) {
    const pending = this.qtyPending.get(lineKey);
    if (!pending) return;

    const { quantity, baseline, card, bundleLines } = pending;
    this.qtyPending.delete(lineKey);

    this.#setQtyLoading(card, true);

    try {
      await this.#syncQuantity(lineKey, quantity, baseline, bundleLines);
    } finally {
      if (card?.isConnected && !this.qtyPending.has(lineKey)) {
        this.#setQtyLoading(card, false);
      }
    }
  }

  async #syncQuantity(lineKey, quantity, rollbackQuantity, bundleLines) {
    if (quantity < 0) return;

    const previousQuantity =
      rollbackQuantity ?? this.#getLineQuantityFromDom(lineKey);

    try {
      // A set moves as one, so its parent and products go up in the same
      // request. Sending them separately would leave the cart holding a
      // half-scaled bundle between the two calls, and priced as one.
      const response = bundleLines?.length
        ? await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              updates: this.#buildBundleUpdates(lineKey, quantity, bundleLines),
            }),
          })
        : await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: lineKey, quantity }),
          });
      const cart = await this.#reconcileFreeGift(await response.json());
      this.#patchCart(cart);
    } catch (error) {
      if (previousQuantity != null && quantity > 0) {
        this.#patchLineQuantity(lineKey, previousQuantity);
      }
      console.error(error);
    }
  }

  #buildBundleUpdates(parentKey, quantity, bundleLines) {
    const updates = { [parentKey]: quantity };

    for (const { key, unit } of bundleLines) {
      updates[key] = quantity > 0 ? unit * quantity : 0;
    }

    return updates;
  }

  #cancelQtyPending(lineKey) {
    const pending = this.qtyPending.get(lineKey);
    if (!pending) return;

    if (pending.timer) clearTimeout(pending.timer);
    this.qtyPending.delete(lineKey);

    if (pending.card?.isConnected) {
      this.#setQtyLoading(pending.card, false);
    }
  }

  #clearAllQtyPending() {
    for (const pending of this.qtyPending.values()) {
      if (pending.timer) clearTimeout(pending.timer);
    }
    this.qtyPending.clear();
  }

  #setQtyLoading(card, isLoading) {
    const qtyEl = card?.querySelector('.pos-cart-drawer__qty');
    if (!qtyEl) return;

    qtyEl.classList.toggle('pos-cart-drawer__qty--loading', isLoading);
    qtyEl.setAttribute('aria-busy', isLoading ? 'true' : 'false');

    for (const button of qtyEl.querySelectorAll('.js-pos-cart-drawer-qty')) {
      button.disabled = isLoading;
    }
  }

  #setRemoveButtonLoading(button, isLoading) {
    if (!button) return;

    button.disabled = isLoading;
    button.classList.toggle('pos-cart-drawer__remove--loading', isLoading);
    button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  #setUpgradeButtonLoading(button, isLoading) {
    if (!button) return;

    button.disabled = isLoading;
    button.classList.toggle('pos-cart-drawer__btn--loading', isLoading);
    button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  async #switchPlan(lineKey, familyId, targetPlanId, trigger) {
    const family = this.#findFamilyById(familyId);
    if (!family) return;

    // A bundle is several lines under a parent, so there is no single line to
    // move onto another plan; the whole set is exchanged instead.
    if (family.isBundle) return this.#switchBundlePlan(lineKey, family, targetPlanId, trigger);

    const targetPlan = family.plans?.find((plan) => Number(plan.sellingPlanId) === Number(targetPlanId));
    if (!targetPlan) return;

    const select = trigger?.closest('[data-plan-select]');
    this.#closeAllPlanSelects();

    try {
      const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await cartResponse.json();
      const item = cart.items.find((entry) => entry.key === lineKey);
      if (!item) return;

      const currentPlanId = Number(item.selling_plan_allocation?.selling_plan?.id);
      if (currentPlanId === Number(targetPlan.sellingPlanId)) return;

      this.#setPlanSelectLoading(select, true);

      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0 }),
      });

      const addBody = {
        items: [
          {
            id: targetPlan.variantId,
            quantity: item.quantity,
            ...(targetPlan.sellingPlanId ? { selling_plan: targetPlan.sellingPlanId } : {}),
          },
        ],
      };

      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(addBody),
      });

      const addData = await addResponse.json();
      if (addData.status) {
        console.error(addData.description);
        return;
      }

      await this.refresh();
    } catch (error) {
      console.error(error);
    } finally {
      this.#setPlanSelectLoading(select, false);
    }
  }

  // Moving a bundle to another supply is a remove-and-re-add of the whole set —
  // the same request shape the card adds with, since every line's variant and
  // plan differ between the options and none of them can be changed in place.
  //
  // Everything hanging off the parent goes first, the free gift included: left
  // behind, it would point at a parent that no longer exists. It is not re-added
  // here — the refresh at the end runs the new cart through #reconcileFreeGift,
  // which re-parents it onto the set that earned it.
  async #switchBundlePlan(lineKey, family, targetPlanId, trigger) {
    const targetPlan = family.plans.find(
      (plan) => Number(plan.sellingPlanId) === Number(targetPlanId)
    );
    if (!targetPlan) return;

    const parentVariantId = Number(targetPlan.parentVariantId);
    const select = trigger?.closest('[data-plan-select]');
    this.#closeAllPlanSelects();

    try {
      const cart = await this.#fetchCart();
      const parentLine = cart.items?.find((entry) => entry.key === lineKey);
      if (!parentLine) return;

      // The parent names the supply, so a set already on the target one has
      // nothing to exchange.
      if (Number(parentLine.variant_id) === parentVariantId) return;

      this.#setPlanSelectLoading(select, true);

      // Adding the same bundle twice merges onto the existing lines rather than
      // making a second set, so the parent's count is what the shopper owns and
      // what has to come back.
      const quantity = parentLine.quantity || 1;
      const nestedKeys = (cart.items || [])
        .filter((entry) => entry.parent_relationship?.parent_key === lineKey)
        .map((entry) => entry.key);

      const updates = {};
      for (const key of [lineKey, ...nestedKeys]) {
        updates[key] = 0;
      }

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });

      // The parent goes in first so the children can point at it by variant id;
      // parent_id only resolves against a line in the same request.
      const items = [{ id: parentVariantId, quantity }];

      for (const entry of targetPlan.items || []) {
        this.setVariantCompareAtPrice(entry.id, entry.compare_at_price);

        items.push({
          id: Number(entry.id),
          quantity,
          parent_id: parentVariantId,
          ...(entry.selling_plan ? { selling_plan: Number(entry.selling_plan) } : {}),
        });
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      const data = await response.json();
      if (data.status) console.error(data.description);

      await this.refresh();
    } catch (error) {
      console.error(error);
    } finally {
      this.#setPlanSelectLoading(select, false);
    }
  }

  async #upgradeItem(lineKey, familyId, button) {
    const family = this.#findFamilyById(familyId);
    if (!family) return;

    const bestPlan = this.#getBestPlan(family.plans);
    if (!bestPlan) return;

    this.#setUpgradeButtonLoading(button, true);

    try {
      await this.#switchPlan(lineKey, familyId, bestPlan.sellingPlanId, button);
    } finally {
      this.#setUpgradeButtonLoading(button, false);
    }
  }

  async #addRecommendation(variantId, sellingPlanId, button) {
    if (!variantId) return;

    this.#setUpgradeButtonLoading(button, true);

    try {
      const body = {
        items: [
          {
            id: Number(variantId),
            quantity: 1,
            ...(sellingPlanId ? { selling_plan: Number(sellingPlanId) } : {}),
          },
        ],
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (data.status) {
        console.error(data.description);
        return;
      }

      await this.refresh();
      this.open();
    } catch (error) {
      console.error(error);
    } finally {
      this.#setUpgradeButtonLoading(button, false);
    }
  }

  #updateCounters(cart) {
    if (window.CartCounterUpdater) {
      window.CartCounterUpdater.update(cart);
      return;
    }

    const itemCount = cart?.item_count ?? 0;
    document.querySelectorAll('.cart-counter-updater').forEach((el) => {
      el.textContent = itemCount;
    });
  }

  #crownIcon(className = '') {
    const icon = this.config.icons?.crown?.trim();
    if (!icon) return '';

    const classes = ['pos-cart-drawer__crown', className].filter(Boolean).join(' ');

    return `<span class="${classes}" aria-hidden="true">${icon}</span>`;
  }

  #giftIcon() {
    const src = this.config.icons?.gift?.trim();
    if (!src) return '';

    return `<img class="pos-cart-drawer__plan-unlocked-gift" src="${escapeHtml(src)}" alt="" width="16" height="16" loading="lazy" decoding="async">`;
  }

  #checkIcon() {
    return `<svg class="pos-cart-drawer__check-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><g transform="translate(-1 -1) scale(1.15)"><path d="M16.8027 6.95105L18.2294 8.83777L14.7294 12.3378L10.5499 16.6234L5.99996 12.0734L7.70972 10.3637L10.5499 13.2038L16.8027 6.95105Z" fill="#FF699F"/></g></svg>`;
  }

  #trashIcon() {
    return `<svg width="13" height="14" viewBox="0 0 13 14" fill="none" aria-hidden="true" focusable="false"><path d="M11.8125 2.1875C11.9401 2.1875 12.0449 2.22852 12.127 2.31055C12.209 2.39258 12.25 2.4974 12.25 2.625V3.0625C12.25 3.1901 12.209 3.29492 12.127 3.37695C12.0449 3.45898 11.9401 3.5 11.8125 3.5H11.375L10.8008 12.7695C10.7826 13.1159 10.6458 13.4076 10.3906 13.6445C10.1354 13.8815 9.83464 14 9.48828 14H2.76172C2.41536 14 2.11458 13.8815 1.85938 13.6445C1.60417 13.4076 1.46745 13.1159 1.44922 12.7695L0.875 3.5H0.4375C0.309896 3.5 0.205078 3.45898 0.123047 3.37695C0.0410156 3.29492 0 3.1901 0 3.0625V2.625C0 2.4974 0.0410156 2.39258 0.123047 2.31055C0.205078 2.22852 0.309896 2.1875 0.4375 2.1875H2.67969L3.60938 0.628906C3.86458 0.209635 4.2474 0 4.75781 0H7.49219C8.0026 0 8.38542 0.209635 8.64062 0.628906L9.57031 2.1875H11.8125ZM4.75781 1.3125L4.21094 2.1875H8.03906L7.49219 1.3125H4.75781ZM9.48828 12.6875L10.0625 3.5H2.1875L2.76172 12.6875H9.48828Z" fill="#999"/></svg>`;
  }

  #clockIcon() {
    return `<svg class="pos-cart-drawer__clock-icon" width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true" focusable="false"><g clip-path="url(#PosCartDrawerClockClip)"><path d="M8.95321 7.43517C8.78011 7.3064 8.53542 7.34236 8.40665 7.51543C8.33583 7.61062 8.26005 7.704 8.1814 7.79297C8.03853 7.95459 8.0537 8.20146 8.21532 8.34435C8.28966 8.41005 8.38194 8.44232 8.4739 8.44232C8.58194 8.44232 8.68952 8.39773 8.76671 8.31041C8.85986 8.20506 8.9496 8.09447 9.03347 7.98172C9.16224 7.80865 9.12632 7.56392 8.95321 7.43517Z" fill="#303030" fill-opacity="0.55"/><path d="M9.60653 5.60326C9.39587 5.55738 9.18764 5.69107 9.14177 5.90185C9.11651 6.01781 9.086 6.13398 9.05102 6.24718C8.98733 6.45331 9.1028 6.67203 9.30891 6.73572C9.34733 6.74757 9.38616 6.75322 9.42436 6.75322C9.59116 6.75322 9.74561 6.64552 9.79745 6.47779C9.83897 6.34345 9.87518 6.20558 9.90514 6.06802C9.95102 5.85724 9.81733 5.64915 9.60653 5.60326Z" fill="#303030" fill-opacity="0.55"/><path d="M7.07302 8.68622C6.9693 8.74411 6.86192 8.79821 6.75387 8.84702C6.55725 8.93581 6.46985 9.16718 6.55864 9.3638C6.62387 9.50825 6.76604 9.59374 6.91487 9.59374C6.96862 9.59374 7.02325 9.58259 7.07542 9.55903C7.2036 9.50114 7.33091 9.43698 7.45387 9.36833C7.64225 9.26317 7.7097 9.0252 7.6045 8.83684C7.49936 8.6485 7.26139 8.58106 7.07302 8.68622Z" fill="#303030" fill-opacity="0.55"/><path d="M4.60937 1.875V4.8382L3.17697 6.27059C3.02443 6.42314 3.02443 6.67047 3.17697 6.82301C3.25325 6.8993 3.3532 6.93742 3.4532 6.93742C3.55316 6.93742 3.65314 6.89928 3.72943 6.82301L5.27622 5.27621C5.34947 5.20297 5.39062 5.10359 5.39062 5V1.875C5.39062 1.65926 5.21574 1.48438 4.99999 1.48438C4.78425 1.48438 4.60937 1.65926 4.60937 1.875Z" fill="#303030" fill-opacity="0.55"/><path d="M9.60938 0.839844C9.39363 0.839844 9.21875 1.01473 9.21875 1.23047V2.31516C8.30953 0.889219 6.7217 0 5 0C3.66445 0 2.40885 0.520098 1.46445 1.46445C0.520098 2.40885 0 3.66445 0 5C0 6.33555 0.520098 7.59115 1.46445 8.53555C2.40885 9.4799 3.66445 10 5 10C5.0033 10 5.00648 9.99959 5.00977 9.99951C5.01305 9.99959 5.01623 10 5.01953 10C5.16031 10 5.30248 9.99406 5.44213 9.98238C5.65711 9.96437 5.8168 9.77551 5.79881 9.56053C5.78078 9.34555 5.59217 9.18578 5.37693 9.20387C5.25889 9.21373 5.13865 9.21875 5.01953 9.21875C5.01623 9.21875 5.01305 9.21916 5.00977 9.21924C5.00648 9.21916 5.0033 9.21875 5 9.21875C2.67377 9.21875 0.78125 7.32623 0.78125 5C0.78125 2.67377 2.67377 0.78125 5 0.78125C6.49918 0.78125 7.87807 1.58033 8.63156 2.85156H7.55748C7.34174 2.85156 7.16686 3.02645 7.16686 3.24219C7.16686 3.45793 7.34174 3.63281 7.55748 3.63281H8.75C8.98789 3.63281 9.21045 3.56596 9.39994 3.45014C9.41213 3.44318 9.42385 3.4357 9.43502 3.42764C9.77498 3.20398 10 2.81928 10 2.38281V1.23047C10 1.01473 9.82512 0.839844 9.60938 0.839844Z" fill="#303030" fill-opacity="0.55"/></g><defs><clipPath id="PosCartDrawerClockClip"><rect width="10" height="10" fill="#fff"/></clipPath></defs></svg>`;
  }

  #chevronIcon() {
    return `<svg width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true" focusable="false"><path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  }
}

const formatMoney = (cents) => {
  const amount = Number(cents);
  if (!Number.isFinite(amount)) return '';

  const dollars = amount / 100;
  const isWhole = amount % 100 === 0;

  if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
    try {
      return Shopify.formatMoney(amount, POS_CART_DRAWER_MONEY_FORMAT);
    } catch (error) {
      // fall through to locale formatting
    }
  }

  const formatted = dollars.toLocaleString('en-US', {
    minimumFractionDigits: isWhole ? 0 : 2,
    maximumFractionDigits: isWhole ? 0 : 2,
  });

  return `$${formatted}`;
};

const escapeHtml = (str) => {
  const div = document.createElement('div');
  div.textContent = str ?? '';
  return div.innerHTML;
};

const trimParentheticalTitle = (str) => {
  const original = String(str ?? '').trim();
  if (!original) return '';

  const trimmed = original.replace(/\s*\([^)]*\)\s*/g, ' ').replace(/\s{2,}/g, ' ').trim();
  return trimmed || original;
};

const formatBulletHtml = (str) => {
  const normalized = String(str ?? '')
    .replace(/&lt;br\s*\/?&gt;/gi, '<br>')
    .replace(/\r\n|\r|\n/g, '<br>')
    .trim();

  const parts = normalized.split(/<br\s*\/?>/gi);
  return parts.map((part) => escapeHtml(part)).join('<br>');
};

const getSizedImageUrl = (url, size) => {
  if (!url) return '';
  const match = url.match(/(.+)\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i);
  if (!match) return url;
  return `${match[1]}_${size}.${match[2]}${match[3] || ''}`;
};

const getPosCartDrawerFamilyCount = (root) => {
  const configEl = root.querySelector('.js-pos-cart-drawer-config');
  if (!configEl) return 0;

  try {
    const config = JSON.parse(configEl.textContent);
    return config.families?.length ?? 0;
  } catch {
    return 0;
  }
};

const initPosCartDrawer = () => {
  const roots = document.querySelectorAll('.js-pos-cart-drawer');
  if (!roots.length) return null;

  let bestRoot = roots[0];
  let bestFamilyCount = getPosCartDrawerFamilyCount(bestRoot);

  for (const root of roots) {
    const familyCount = getPosCartDrawerFamilyCount(root);
    if (familyCount > bestFamilyCount) {
      bestRoot = root;
      bestFamilyCount = familyCount;
    }
  }

  for (const root of roots) {
    if (root === bestRoot) continue;
    root.setAttribute('aria-hidden', 'true');
    root.classList.add('pos-cart-drawer--duplicate');
  }

  const instance = new PosCartDrawer(bestRoot);
  window.posCartDrawerInstance = instance;
  return instance;
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPosCartDrawer);
} else {
  initPosCartDrawer();
}